import { inject, Injectable, Injector } from '@angular/core';
import { GameService } from '@core/game/game.service';
import { Wk8BookService } from '@core/wk8/wk8-book.service';
import { LpMissionService } from '@features/lp/lp-mission/lp-mission.service';
import { LpVariablesService } from '@features/lp/lp-variables/lp-variables.service';
import { LpCondition, LpConditions } from '@typings/lp/generated';
import { Uid } from '@typings/id';

export type IConditionContext = {
  DRAG_AND_DROP_ACTIVITY?: {
    targets: Record<string, string | undefined>;
  };
};

@Injectable({
  providedIn: 'root',
})
export class LpConditionsService {
  readonly #injector = inject(Injector);

  get _gameService() {
    return this.#injector.get(GameService);
  }

  get _lpMissionService() {
    return this.#injector.get(LpMissionService);
  }

  get _lpVariablesService() {
    return this.#injector.get(LpVariablesService);
  }

  // TODO: figure out if there is a more intuitive way to handle this
  public subscribe() {
    this._gameService.subscribe();
  }

  public async evaluate(_groupOrCondition: LpConditions | LpCondition, ctx?: IConditionContext): Promise<boolean> {
    if (_groupOrCondition.dataType === 'Conditions') {
      return await this.evaluateGroup(_groupOrCondition, ctx);
    } else {
      return await this.evaluateCondition(_groupOrCondition, ctx);
    }
  }

  private async evaluateGroup(group: LpConditions, ctx?: IConditionContext): Promise<boolean> {
    const results = await Promise.all(
      group.conditions.map(async (condition) => {
        return await this.evaluateCondition(condition, ctx);
      }),
    );

    switch (group.logicOperator) {
      case 'And':
        return results.every((res) => res);
      case 'Or':
        return results.some((res) => res);
      default:
        throw new Error(`Logic operator '${group.logicOperator}' not supported`);
    }
  }

  private async evaluateCondition(condition: LpCondition, ctx?: IConditionContext): Promise<boolean> {
    // NOTE: try to keep the order of the if statements alphabetical

    if (condition.dataType === 'HasItemInInventoryCondition') {
      const hasItemInInventory = this._gameService.inventoryUids().includes(condition.item.uid as Uid);
      return condition.invert ? !hasItemInInventory : hasItemInInventory;
    }

    if (condition.dataType === 'InvertCondition') {
      const result = await this.evaluate(condition.condition, ctx);
      return !result;
    }

    if (condition.dataType === 'IsCanvasElementPressingCondition') {
      console.warn('IsCanvasElementPressingCondition is not implemented yet, always returning true');
      return true; // TODO: implement this condition
    }

    if (condition.dataType === 'IsCanvasElementDraggingCondition') {
      console.warn('IsCanvasElementDraggingCondition is not implemented yet, always returning true');
      return true; // TODO: implement this condition
    }

    if (condition.dataType === 'IsDraggableInTargetCondition') {
      const isInTarget = ctx?.DRAG_AND_DROP_ACTIVITY?.targets[condition.target.uid] === condition.draggable.uid;
      return condition.invert ? !isInTarget : isInTarget;
    }

    if (condition.dataType === 'IsMissionFinishedCondition') {
      const isFinished = this._lpMissionService.finishedMissions().some((mission) => mission.uid === condition.mission.uid);
      return condition.invert ? !isFinished : isFinished;
    }

    if (condition.dataType === 'VariableComparison') {
      const firstValue = this._lpVariablesService.tryGet(condition.variable1.variableRef) ?? '';
      const secondValue = condition.variable2 ? (this._lpVariablesService.tryGet(condition.variable2.variableRef) ?? '') : condition.value;
      switch (condition.operator) {
        case 'EqualTo':
          return firstValue === secondValue;
        case 'GreaterOrEqualTo':
          return parseFloat(firstValue) >= parseFloat(secondValue);
        case 'GreaterThan':
          return parseFloat(firstValue) > parseFloat(secondValue);
        case 'NotEqualTo':
          return firstValue !== secondValue;
        case 'SmallerOrEqualTo':
          return parseFloat(firstValue) <= parseFloat(secondValue);
        case 'SmallerThan':
          return parseFloat(firstValue) < parseFloat(secondValue);
        default:
          throw new Error(`Operator '${condition.operator}' not supported`);
      }
    }

    if (condition.dataType === 'Wk8IsBookCompleteCondition') {
      const bookService = this.#injector.get(Wk8BookService);
      await bookService.waitForLoad();
      const minimumCompletion = condition.percentage ?? 100;
      const isComplete = bookService.getBookCompletionPercentage(condition.book) >= minimumCompletion / 100;
      return condition.invert ? !isComplete : isComplete;
    }

    throw new Error(`Condition '${condition.dataType}' not supported`);
  }
}
